// Settings → Message Templates list (Wave E).
//
// Mirrors the implicit "Message Templates" surface in legacy
// `tricivo_system/operations/settings.php`. Pre-defined templates per
// (channel_type, template_key) with body editing on the detail page.
//
// Wrapped in <SubscriptionGate feature="custom-templates"> by the router.

import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Eye, FileText, Pencil } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { FilterBar } from '../../../components/ui/FilterBar/FilterBar';
import { Table, type Column, type RowAction } from '../../../components/ui/Table/Table';
import { Pagination } from '../../../components/ui/Pagination/Pagination';
import { Badge } from '../../../components/ui/Badge/Badge';
import { Select } from '../../../components/form/Select';
import { Switch } from './components/Switch';
import { TemplatePreviewModal } from './components/TemplatePreviewModal';
import { useTemplatesList, useToggleTemplateActive } from './hooks/useTemplates';
import type { TemplateInfo } from '../../../types/api';
import styles from './TemplatesListPage.module.css';

const CHANNEL_LABELS: Record<string, string> = {
  email: 'Email',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  push: 'Push',
};

export function TemplatesListPage(): JSX.Element {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const toggleMutation = useToggleTemplateActive();
  const [previewTarget, setPreviewTarget] = useState<TemplateInfo | null>(null);

  const channel = params.get('channel') ?? '';
  const q = params.get('q') ?? '';
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: false });
  }

  const list = useTemplatesList({
    page,
    channel_type: channel
      ? (channel as 'email' | 'sms' | 'whatsapp' | 'push')
      : undefined,
    q: q || undefined,
  });

  const templates = list.data?.templates ?? [];
  const pagination = list.data?.pagination;
  const total = pagination?.total ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  const columns: Column<TemplateInfo>[] = useMemo(
    () => [
      {
        key: 'template_name',
        header: 'Template',
        render: (_v, row) => (
          <button
            type="button"
            className={styles.refLink}
            onClick={() =>
              navigate(`/settings/templates/${encodeURIComponent(row.uuid)}`)
            }
          >
            {row.template_name}
          </button>
        ),
      },
      { key: 'template_key', header: 'Key' },
      {
        key: 'channel_type',
        header: 'Channel',
        render: (_v, row) => (
          <Badge variant="info">
            {CHANNEL_LABELS[row.channel_type] ?? row.channel_type}
          </Badge>
        ),
      },
      {
        key: 'is_active',
        header: 'Active',
        render: (_v, row) => (
          <Switch
            checked={row.is_active}
            onChange={(next) =>
              toggleMutation.mutate({ uuid: row.uuid, is_active: next })
            }
            ariaLabel={`Toggle ${row.template_name}`}
            label={row.is_active ? 'Active' : 'Inactive'}
          />
        ),
      },
      {
        key: 'is_default',
        header: 'Default',
        render: (_v, row) => (row.is_default ? 'Yes' : 'No'),
      },
    ],
    [navigate, toggleMutation],
  );

  const rowActions = (row: TemplateInfo): RowAction<TemplateInfo>[] => [
    {
      label: 'Preview',
      icon: Eye,
      onClick: () => setPreviewTarget(row),
      variant: 'ghost',
    },
    {
      label: 'Edit',
      icon: Pencil,
      onClick: () =>
        navigate(`/settings/templates/${encodeURIComponent(row.uuid)}`),
      variant: 'ghost',
    },
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        title="Notification Templates"
        subtitle={`${total} template${total === 1 ? '' : 's'}`}
        actions={
          <Button
            variant="ghost"
            icon={ArrowLeft}
            onClick={() => navigate('/settings')}
          >
            Back
          </Button>
        }
      />

      <Card flush>
        <FilterBar
          search={{
            value: q,
            onChange: (v) => setParam('q', v),
            placeholder: 'Search by template name or key…',
            ariaLabel: 'Search templates',
          }}
        >
          <Select
            aria-label="Channel filter"
            value={channel}
            onChange={(e) => setParam('channel', e.target.value)}
            options={[
              { value: '', label: 'All channels' },
              { value: 'email', label: 'Email' },
              { value: 'sms', label: 'SMS' },
              { value: 'whatsapp', label: 'WhatsApp' },
              { value: 'push', label: 'Push' },
            ]}
          />
        </FilterBar>

        <Table
          columns={columns}
          data={templates as TemplateInfo[]}
          keyField={(row) => row.uuid}
          loading={list.isLoading}
          emptyState={
            <div className={styles.empty}>
              <FileText size={28} aria-hidden />
              <h3>No templates found</h3>
              <p>Adjust your filters or check the seed data.</p>
            </div>
          }
          rowActions={rowActions}
          ariaLabel="Templates"
        />

        {totalPages > 1 ? (
          <div className={styles.paginationWrap}>
            <Pagination
              pagination={{ page, limit: pagination?.limit ?? 50, total, totalPages }}
              onPageChange={(next) => setParam('page', String(next))}
            />
          </div>
        ) : null}
      </Card>

      <TemplatePreviewModal
        open={previewTarget !== null}
        onClose={() => setPreviewTarget(null)}
        template={previewTarget}
      />
    </div>
  );
}
